// Plain <textarea> with a tiny toolbar above it. Bold / italic / underline /
// link buttons wrap the current selection in markdown markers. Keyboard
// shortcuts ⌘B / ⌘I / ⌘U / ⌘K do the same. The stored value is plain
// text with markdown markers — never rich state — so save/load is a string
// roundtrip with no surprises.

import { useRef } from 'react';
import { Bold, Italic, Underline, Link as LinkIcon } from 'lucide-react';

type Mark = 'bold' | 'italic' | 'underline' | 'link';

const MARKERS: Record<Exclude<Mark, 'link'>, string> = {
  bold: '**',
  italic: '*',
  underline: '__',
};

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}

export default function RichTextArea({ value, onChange, placeholder, rows = 3 }: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  function applyMark(mark: Mark) {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);

    let inserted: string;
    let cursorStart: number;
    let cursorEnd: number;

    if (mark === 'link') {
      const url = window.prompt('Link URL', 'https://');
      if (!url) return;
      const text = selected || 'link text';
      inserted = `[${text}](${url})`;
      // Select just the visible text so the user can immediately edit it
      cursorStart = before.length + 1;
      cursorEnd = cursorStart + text.length;
    } else {
      const m = MARKERS[mark];
      // Toggle: if selection is already wrapped, unwrap it.
      if (
        selected.length >= m.length * 2 &&
        selected.startsWith(m) &&
        selected.endsWith(m)
      ) {
        inserted = selected.slice(m.length, -m.length);
        cursorStart = before.length;
        cursorEnd = cursorStart + inserted.length;
      } else if (
        before.endsWith(m) &&
        after.startsWith(m)
      ) {
        // Cursor sits inside an existing pair — strip it.
        const newBefore = before.slice(0, -m.length);
        const newAfter = after.slice(m.length);
        const next = newBefore + selected + newAfter;
        onChange(next);
        requestAnimationFrame(() => {
          if (!ref.current) return;
          ref.current.focus();
          const pos = newBefore.length + selected.length;
          ref.current.setSelectionRange(pos, pos);
        });
        return;
      } else {
        const text = selected || (mark === 'bold' ? 'bold' : mark === 'italic' ? 'italic' : 'underlined');
        inserted = `${m}${text}${m}`;
        cursorStart = before.length + m.length;
        cursorEnd = cursorStart + text.length;
      }
    }

    const next = before + inserted + after;
    onChange(next);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.setSelectionRange(cursorStart, cursorEnd);
    });
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k === 'b') { e.preventDefault(); applyMark('bold'); }
    else if (k === 'i') { e.preventDefault(); applyMark('italic'); }
    else if (k === 'u') { e.preventDefault(); applyMark('underline'); }
    else if (k === 'k') { e.preventDefault(); applyMark('link'); }
  }

  return (
    <div>
      <div className="flex items-center gap-0.5 mb-1 px-0.5">
        <ToolbarButton title="Bold (⌘B)" onClick={() => applyMark('bold')}><Bold size={11} /></ToolbarButton>
        <ToolbarButton title="Italic (⌘I)" onClick={() => applyMark('italic')}><Italic size={11} /></ToolbarButton>
        <ToolbarButton title="Underline (⌘U)" onClick={() => applyMark('underline')}><Underline size={11} /></ToolbarButton>
        <ToolbarButton title="Link (⌘K)" onClick={() => applyMark('link')}><LinkIcon size={11} /></ToolbarButton>
      </div>
      <textarea
        ref={ref}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-white resize-y font-mono"
        style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: '13px' }}
      />
    </div>
  );
}

function ToolbarButton({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800"
    >
      {children}
    </button>
  );
}
